import { parseMoney, priceMinutes } from './money.js'
import type { SolutionPricingDto } from '../dto/problem-solution.dto.js'
import type { SolutionPricingBasis } from '../enums/solution-pricing-basis.enum.js'
import {
  linearFeetSchema,
  trunkDiameterSchema,
} from '../validators/decimal-string.validator.js'

/** What a surveyor recorded for one solution on one tree. */
export interface SolutionQuantities {
  /** Crew minutes, when the solution is charged by time. */
  minutes: number | null
  /** Feet, as an exact decimal string, when charged by length. */
  linearFeet: string | null
  /** The TREE's diameter in inches, when charged by DBH. */
  dbhInches: string | null
}

/** A basis whose quantity is a measurement, not a count of one tree or of minutes. */
type MeasuredBasis = Exclude<SolutionPricingBasis, 'TIME' | 'PER_TREE'>

/**
 * The measurement a basis prices, or null when there is none or it is not
 * one. The decimal schemas are what a foot and a diameter are; `Number`
 * would read "" as zero and a solution with no measurement as free.
 */
function measuredAmount(
  basis: MeasuredBasis,
  quantities: SolutionQuantities,
): number | null {
  switch (basis) {
    case 'LINEAR_FEET':
      return readDecimal(linearFeetSchema, quantities.linearFeet)
    case 'DBH_INCH':
      return readDecimal(trunkDiameterSchema, quantities.dbhInches)
    default: {
      const unhandled: never = basis
      return unhandled
    }
  }
}

function readDecimal(
  schema: typeof linearFeetSchema,
  value: string | null,
): number | null {
  if (value === null) return null
  const parsed = schema.safeParse(value)
  if (!parsed.success) return null
  const amount = Number(parsed.data)
  return Number.isFinite(amount) ? amount : null
}

/** The rates a solution is priced against, plus the fallback for time. */
export interface SolutionPricingContext {
  pricings: SolutionPricingDto[]
  /** The organization's tree labor rate, used when TIME has no override. */
  organizationRatePerHour: string | null
}

/**
 * What one solution costs on one tree, in cents.
 *
 * Shared rather than implemented on each side, because the survey screen shows
 * a running total as somebody types and the API freezes the figure into a sent
 * proposal. Two implementations of the same arithmetic is two answers to "what
 * does this cost", and the one a client sees would be the one nobody checked.
 *
 * Null means "nothing here can be priced yet": no basis carries both a rate
 * and a quantity. That is different from zero, which is a real price for work
 * somebody has decided to do for nothing, and the caller renders them
 * differently.
 *
 * Bases sum. A solution charged by time AND by the foot is labor plus
 * materials, and that is the case the multi-basis model exists for. A flat
 * per-tree fee PLUS the foot is the same case with the labor replaced by a
 * fixed amount, and needs nothing of its own.
 */
export function priceSolution(
  context: SolutionPricingContext,
  quantities: SolutionQuantities,
): number | null {
  let total = 0
  let priced = false

  for (const pricing of context.pricings) {
    // TIME alone falls back to the organization's rate. Every other basis
    // carries its own rate; one that does not is not a pricing.
    const rate =
      pricing.basis === 'TIME'
        ? parseMoney(pricing.unitRate ?? context.organizationRatePerHour)
        : parseMoney(pricing.unitRate)
    if (rate === null) continue

    if (pricing.basis === 'PER_TREE') {
      // One tree, so the quantity is one and there is nothing to wait for: it
      // prices whatever else has or has not been recorded yet.
      //
      // Once per tree needs no bookkeeping here, because a solution can only
      // ever be recorded once per tree: a tree carries at most one problem of
      // each type, a solution belongs to exactly one type, and a problem
      // carries each solution at most once. Those rules live in the tree input
      // schema and `TreeService.assertSolutionsBelong`; if either is ever
      // relaxed, this is the line that would start charging twice.
      total += rate
      priced = true
      continue
    }

    if (pricing.basis === 'TIME') {
      if (quantities.minutes === null) continue
      total += priceMinutes(quantities.minutes, rate)
      priced = true
      continue
    }

    const amount = measuredAmount(pricing.basis, quantities)
    if (amount === null) continue

    // Rounded per component rather than once at the end: the components are
    // different units against different rates, so there is no shared
    // denominator to defer the rounding to.
    total += Math.round(amount * rate)
    priced = true
  }

  return priced ? total : null
}

/**
 * The crew minutes a set of solutions adds to a tree.
 *
 * Separate from the money because minutes are also an estimate of duration,
 * which the schedule shows whether or not a rate exists to price them.
 */
export function solutionMinutes(
  solutions: { minutes: number | null }[],
): number {
  return solutions.reduce((sum, solution) => sum + (solution.minutes ?? 0), 0)
}
